/**
 * App-wide gates and overlays driven by platform services (mounted once in App.tsx):
 *   ManagedDeviceBlock  managed / supervised device + live config blockManagedDevices (#32)
 *   UpdateGate          forced (blocking) or optional (dismissible) update from GET /config (#33)
 *   PendingPurchase     Ask to Buy: "waiting for a grown-up" overlay, re-checked on focus (#32)
 * Also starts push registration, the widget bridge and the device-policy probe.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { APP_BUILD, useApp } from '../../state/store';
import { useT } from '../../lib/i18n';
import { isNative, platform } from '../../lib/platform';
import { initPush, setPushNavigator } from '../../lib/push';
import { initWidget } from '../../lib/widget';
import { probeDevicePolicy, useDeviceGate } from '../../lib/devicePolicy';
import { checkPendingPurchase } from '../../lib/purchases';
import { useParentalGate } from '../parents/ParentalGate';
import { Scribble } from '../../components/characters';
import { Button, Modal, StarsLayer, toast } from '../../components/ui';
import { AppUpdateAnnouncement } from '../../anim/AppUpdate';
import { DeferredPurchase, type DeferredState } from '../../anim';

const IOS_APP_ID = import.meta.env.VITE_IOS_APP_STORE_ID as string | undefined;
const ANDROID_PACKAGE = (import.meta.env.VITE_ANDROID_PACKAGE as string | undefined) || 'app.sketchling.draw';

/** Store page for this platform (null on the web: a reload picks up the new build). */
export function storeUrl(): string | null {
  if (platform === 'ios') return IOS_APP_ID ? `https://apps.apple.com/app/id${IOS_APP_ID}` : 'https://apps.apple.com/';
  if (platform === 'android') return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
  return null;
}

function openStore() {
  const url = storeUrl();
  if (!url) {
    // Web / PWA: the service worker auto-updates; a reload activates the new version.
    window.location.reload();
    return;
  }
  window.open(url, '_blank', 'noopener');
}

export function PlatformServices() {
  const nav = useNavigate();
  useEffect(() => {
    setPushNavigator((p) => nav(p));
    initPush();
    initWidget((p) => nav(p));
    void probeDevicePolicy();
    if (isNative) {
      const h = CapApp.addListener('resume', () => void probeDevicePolicy());
      return () => void h.then((x) => x.remove());
    }
  }, [nav]);
  return (
    <>
      <PendingPurchaseOverlay />
      <UpdateGate />
      <ManagedDeviceBlock />
    </>
  );
}

function ManagedDeviceBlock() {
  const t = useT();
  const gate = useDeviceGate();
  if (gate !== 'blocked') return null;
  return (
    <div className="full bg-night svc-block" data-testid="managed-block" role="alertdialog" aria-labelledby="managed-title">
      <StarsLayer count={10} />
      <div className="col center svc-block-body">
        <Scribble mood="sleepy" size={120} />
        <h1 id="managed-title">{t('svc.managed.title')}</h1>
        <p className="muted">{t('svc.managed.body')}</p>
        <a className="btn btn-secondary btn-small" href="mailto:hello@sketchling.app?subject=Sketchling%20managed%20device">
          {t('svc.managed.help')}
        </a>
      </div>
    </div>
  );
}

const DISMISS_KEY = 'sketchling-update-dismissed';

function UpdateGate() {
  const t = useT();
  const server = useApp((s) => s.appUpdate);
  const sim = useApp((s) => s.cheats.simUpdate);
  const gate = sim !== 'none' ? sim : server;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === String(APP_BUILD);
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (gate !== 'none') useApp.getState().logEvent('App Update Shown', { gate, build: APP_BUILD });
  }, [gate]);
  const later = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, String(APP_BUILD));
    } catch {
      /* ignore */
    }
  }, []);
  const cta = storeUrl() ? t('svc.update.now') : t('svc.update.reload');
  // Store pages are external links: parental gate first on native (P0-3); the web just reloads.
  const pgate = useParentalGate();
  const toStore = () => (storeUrl() ? pgate.guard('store', openStore) : openStore());

  // Web / PWA: the animated announcement (its CTA reloads into the new service-worker build).
  if (!isNative && (gate === 'force' || (gate === 'optional' && !dismissed))) return <AppUpdateAnnouncement mode={gate} onClose={later} />;
  if (gate === 'force')
    return (
      <div className="full bg-night svc-block" data-testid="update-force" role="alertdialog" aria-labelledby="update-title">
        <StarsLayer count={12} />
        <div className="col center svc-block-body">
          <Scribble mood="wave" size={120} />
          <h1 id="update-title">{t('svc.update.forceTitle')}</h1>
          <p className="muted">{t('svc.update.forceBody')}</p>
          <Button onClick={toStore} data-testid="update-cta">
            {cta}
          </Button>
        </div>
        {pgate.element}
      </div>
    );
  if (gate === 'optional' && !dismissed)
    return (
      <Modal onClose={later} label={t('svc.update.optionalTitle')} testId="update-optional">
        <div className="col center svc-sheet">
          <Scribble mood="happy" size={96} />
          <h2>{t('svc.update.optionalTitle')}</h2>
          <p className="muted">{t('svc.update.optionalBody')}</p>
          <div className="row">
            <Button variant="secondary" onClick={later} data-testid="update-later">
              {t('svc.update.later')}
            </Button>
            <Button onClick={toStore} data-testid="update-cta">
              {cta}
            </Button>
          </div>
        </div>
        {pgate.element}
      </Modal>
    );
  return null;
}

/** Overlay shown while a purchase waits for a parent; it re-checks on focus and every minute. */
function PendingPurchaseOverlay() {
  const t = useT();
  const pending = useApp((s) => s.pendingPurchase);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  /** Short animated outcome (sent / approved / denied), then it clears itself. */
  const [flash, setFlash] = useState<DeferredState | null>(null);
  const since = pending?.since;

  useEffect(() => {
    if (!since || Date.now() - new Date(since).getTime() > 5000) return;
    setFlash('sent');
  }, [since]);
  useEffect(() => {
    if (!flash) return;
    const tm = setTimeout(() => setFlash(null), 1800);
    return () => clearTimeout(tm);
  }, [flash]);

  const check = useCallback(
    async (manual: boolean) => {
      setBusy(manual);
      const r = await checkPendingPurchase();
      setBusy(false);
      if (r === 'approved') (setFlash('approved'), toast(t('svc.pending.approved')));
      else if (r === 'expired') toast(t('svc.pending.expired'));
      else if (r === 'declined') (setFlash('denied'), toast(t('svc.pending.declined')));
      else if (manual) toast(t('svc.pending.still'));
    },
    [t],
  );

  useEffect(() => {
    if (!pending) return;
    setOpen(true);
    const iv = setInterval(() => void check(false), 60_000);
    const onFocus = () => void check(false);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [pending, check]);

  if (flash) return <DeferredPurchase state={flash} onClose={() => setFlash(null)} />;
  if (!pending) return null;
  if (!open)
    return (
      <button type="button" className="chip svc-pending-chip" data-testid="pending-chip" onClick={() => setOpen(true)}>
        {t('svc.pending.chip')}
      </button>
    );
  const planName = t(pending.plan === 'family' ? 'pay.family' : 'pay.individual');
  return (
    <Modal onClose={() => setOpen(false)} label={t('svc.pending.title')} testId="pending-purchase">
      <div className="col center svc-sheet">
        <Scribble mood="curious" size={100} className="float" />
        <h2>{t('svc.pending.title')}</h2>
        <p className="muted">{t('svc.pending.body', { plan: planName })}</p>
        <div className="row">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('ok')}
          </Button>
          <Button onClick={() => void check(true)} disabled={busy} data-testid="pending-check">
            {t('svc.pending.check')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
